import { useCallback, useEffect, useRef, useState } from "react";
import { getUniqueId } from "@jugl-web/utils";
import {
  useRestApiProvider,
  type UsePaginatedRequestHookParams,
  type UsePaginatedRequestHookReturn,
} from "@jugl-web/rest-api";
import { TicketLogModel } from "@jugl-web/rest-api/tickets/models/TicketLog";

export type UseTicketLogsParams = UsePaginatedRequestHookParams<{
  entityId: string;
  ticketId: string;
}>;

export type UseTicketLogsReturn = UsePaginatedRequestHookReturn<TicketLogModel>;

export const useTicketLogs = ({
  entityId,
  ticketId,
  pageSize = 20,
  skip,
}: UseTicketLogsParams): UseTicketLogsReturn => {
  const { ticketsApi } = useRestApiProvider();
  const requestId = useRef<string>(undefined);

  const [logs, setLogs] = useState<TicketLogModel[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState<number | null>(null);
  const [isError, setIsError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [fetchTicketLogs] = ticketsApi.useLazyGetTicketLogsQuery();

  const loadPage = useCallback(
    async (page: number) => {
      if (!entityId || !ticketId) return;

      try {
        setIsError(false);
        setIsLoading(true);

        const newRequestId = getUniqueId();
        requestId.current = newRequestId;

        const result = await fetchTicketLogs({
          entityId,
          ticketId,
          page,
          page_size: pageSize,
        }).unwrap();

        if (requestId.current !== newRequestId) return;

        if (result) {
          setLogs((prev) => {
            if (page === 1) {
              return result.data;
            }

            const existingIds = new Set(prev.map((log) => log.id));
            const newLogs = result.data.filter(
              (log) => !existingIds.has(log.id)
            );

            return [...prev, ...newLogs];
          });

          setTotalPages(result.total_pages);
          setCurrentPage(page);
        }
      } catch {
        setIsError(true);
      } finally {
        setIsLoading(false);
      }
    },
    [entityId, ticketId, pageSize, fetchTicketLogs]
  );

  const loadMore = useCallback(async () => {
    if (isLoading) return;
    if (totalPages !== null && currentPage >= totalPages) return;
    await loadPage(currentPage + 1);
  }, [isLoading, currentPage, totalPages, loadPage]);

  const reset = useCallback(() => {
    setLogs([]);
    setCurrentPage(1);
    setTotalPages(null);
    setIsError(false);
    loadPage(1);
  }, [loadPage]);

  const retry = useCallback(async () => {
    const pageToRetry =
      currentPage === 1 && logs.length === 0 ? 1 : currentPage + 1;
    await loadPage(pageToRetry);
  }, [currentPage, logs.length, loadPage]);

  useEffect(() => {
    if (skip) return;
    reset();
  }, [reset, skip, ticketId]);

  const hasMore = totalPages !== null && currentPage < totalPages;
  const isLoadingMore = isLoading && !!logs.length;
  const isLoadingMoreError = isError && (currentPage > 1 || logs.length === 0);

  return {
    data: logs,
    isLoading,
    isLoadingMore,
    isError,
    isLoadingMoreError,
    hasMore,
    loadMore,
    reset,
    retry,
  };
};
